import React, { useState, useEffect, useRef } from "react";
import { UncontrolledTooltip } from "reactstrap";
import PropTypes from "prop-types";
import cx from "classnames";

import userAvatar from "../../assets/images/ui-user.svg";
import LoadFlagImage from "./LoadFlagImage";
import global from "../../scss/dhp.scss";

import {
  ANALYTICS_DATA_TYPE_BLOCK,
  ANALYTICS_DATA_TYPE_COUNTRY,
  ANALYTICS_DATA_TYPE_LINK,
  ANALYTICS_DATA_TYPE_TEXT,
  ANALYTICS_DATA_TYPE_VISITOR,
} from "../../constants/editor";

let style = Object.assign({}, global);

const normalizeUrl = (url) => {
  if (!url) return "#";
  if (!/^https?:\/\//i.test(url)) {
    return "https://" + url;
  }
  return url;
};

const VisitorImage = ({ visitorImg }) => {
  const [thumb, setThumb] = useState();

  useEffect(() => {
    setThumb({
      src: visitorImg || userAvatar,
      loading: Boolean(visitorImg),
      default: !Boolean(visitorImg),
    });

    if (!visitorImg) return; // no img to preload

    const image = new Image();
    image.src = visitorImg;
    image.onload = () => setThumb(prev => ({ ...prev, loading: false }));
    image.onerror = () => setThumb({ src: userAvatar, loading: false, default: true });
  }, [visitorImg]);

  return (
    <figure className={cx(style["user-thumb"], style["mr-2"])}>
      {thumb && !thumb?.loading && (
        <img
          src={thumb?.src}
          className={cx(style["img-fluid"], {
            [style["default"]]: thumb?.default,
          })}
          alt="visitor image"
        />
      )}
    </figure>
  );
};

const TableFormatter = ({ type, slug, data, rowNo }) => {
  let result = "";
  const colId = `tooltip_col__${rowNo}__${slug}`;
  const tooltipRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (tooltipRef?.current && data?.uniqueHash) {
      setShowTooltip(tooltipRef?.current?.scrollWidth > tooltipRef?.current?.offsetWidth);
    }
  }, [tooltipRef?.current, data?.uniqueHash]);

  switch (type) {
    case ANALYTICS_DATA_TYPE_VISITOR:
      result = (
        <div className={cx(style["d-flex"], style["align-items-center"])}>
          <VisitorImage visitorImg={data?.visitor_image} />
          <h6 id={colId} ref={tooltipRef} className={cx(style["font-14"], style["mb-0"], style["text-truncate"])}>
            {data.visitor_name}
          </h6>

          {showTooltip && (
            <UncontrolledTooltip placement="bottom" target={colId} boundariesElement={document.getElementById("app")}>
              {data.visitor_name}
            </UncontrolledTooltip>
          )}
        </div>
      );

      break;

    case ANALYTICS_DATA_TYPE_BLOCK:
      const defaultScale = 0.08;
      const targetWidth = 48; // 3rem with of "section-view"
      const originalWidth = data?.block_dimension?.w;
      const scaleFactor = originalWidth ? targetWidth / originalWidth : defaultScale;

      result = (
        <div className={cx(style["block-thumb"], style["d-flex"], style["align-items-center"])}>
          <div className={cx(style["section-wrapper"], style["mb-0"], style["mr-2"])}>
            <div
              className={style["section-view"]}
              style={{
                transform: `scale(${scaleFactor})`,
                transformOrigin: "left top",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
              dangerouslySetInnerHTML={{ __html: data.block_html }}></div>
          </div>
          <span id={colId} ref={tooltipRef} className={cx(style["text-truncate"], style["block-name"])}>
            {data.block_name}
          </span>

          {showTooltip && (
            <UncontrolledTooltip placement="bottom" target={colId} boundariesElement={document.getElementById("app")}>
              {data.block_name}
            </UncontrolledTooltip>
          )}
        </div>
      );
      break;

    case ANALYTICS_DATA_TYPE_COUNTRY:
      result = (
        <p className={cx(style["m-0"], style["value"], style["d-flex"], style["align-items-center"])}>
          <LoadFlagImage isoCode={data.country_code} className={style["mr-2"]} />
          <span id={colId} ref={tooltipRef} className={style["text-truncate"]}>
            {data.country_name}
          </span>

          {showTooltip && (
            <UncontrolledTooltip placement="bottom" target={colId} boundariesElement={document.getElementById("app")}>
              {data.country_name}
            </UncontrolledTooltip>
          )}
        </p>
      );

      break;

    case ANALYTICS_DATA_TYPE_LINK:
      const linkUrl = data?.link_type === "Email" ? `mailto:${data.link}` : data.link;

      result = (
        <span id={colId} ref={tooltipRef} className={style["text-truncate"]}>
          <span>
            <a href={normalizeUrl(linkUrl)} target="_blank" rel="noopener noreferrer">
              {data.link}
            </a>
          </span>

          {showTooltip && (
            <UncontrolledTooltip placement="bottom" target={colId} boundariesElement={document.getElementById("app")}>
              {data.link}
            </UncontrolledTooltip>
          )}
        </span>
      );

      break;

    case ANALYTICS_DATA_TYPE_TEXT:
      result = (
        <span id={colId} ref={tooltipRef} className={cx(style["text-truncate"], style["pr-3"])}>
          <span>{data?.[slug]}</span>

          {showTooltip && (
            <UncontrolledTooltip placement="bottom" target={colId} boundariesElement={document.getElementById("app")}>
              {data?.[slug]}
            </UncontrolledTooltip>
          )}
        </span>
      );

      break;

    default:
      break;
  }

  return result;
};

VisitorImage.propTypes = {
  visitorImg: PropTypes.string.isRequired,
};

TableFormatter.propTypes = {
  type: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  rowNo: PropTypes.number.isRequired,
  data: PropTypes.object.isRequired,
};

export default TableFormatter;
